import { Button } from '@neo/primitives/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@neo/primitives/dialog'
import { CheckCircle2, Terminal } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'router-v6'

// CLIAuthorizedModal is the success acknowledgement shown on /home after the
// Plivo CLI completes its browser-login round-trip. The CLI's local loopback
// listener captures (state, code) from the consent screen redirect, then
// 302s the browser back to `/home?cli_authorized=1[&device=<machine>]` so
// the user lands on a Plivo-branded confirmation instead of the CLI's bare
// localhost HTML.
//
// The modal opens once per landing — on dismiss we strip the query param so
// a page refresh doesn't re-show it, and bookmarks of /home are unaffected.
export const CLIAuthorizedModal = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const flag = searchParams.get('cli_authorized')
  const device = (searchParams.get('device') ?? '').trim()
  const [open, setOpen] = useState(flag === '1')

  // Sync the flag → open state in case the URL changes after mount (e.g.
  // user navigates back/forward). Keeps the modal authoritative.
  useEffect(() => {
    if (flag === '1') setOpen(true)
  }, [flag])

  const handleClose = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearchParams(
        (prev) => {
          prev.delete('cli_authorized')
          prev.delete('device')
          return prev
        },
        { replace: true },
      )
    }
  }

  if (flag !== '1') return null

  const deviceLabel = device || 'your machine'

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="cli-authorized-modal">
        <div className="flex flex-col items-center text-center gap-4 py-4">
          <div className="relative">
            <div className="rounded border border-status-success/25 bg-status-success/[0.08] p-4">
              <Terminal size={32} className="text-status-success" />
            </div>
            <CheckCircle2
              size={20}
              className="absolute -bottom-1 -right-1 text-status-success bg-background rounded-full"
            />
          </div>
          <div className="space-y-1">
            <DialogTitle className="text-h3-500">Plivo CLI authorized</DialogTitle>
            <DialogDescription className="text-p-400 text-secondary">
              The Plivo CLI on {deviceLabel} can now act on your behalf using your Plivo Account
              key. You can close that terminal prompt and start running commands.
            </DialogDescription>
          </div>
          <Button
            variant="default"
            size="lg"
            onClick={() => handleClose(false)}
            data-testid="cli-authorized-modal-dismiss-btn"
            className="w-full"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
